import type { ReactNode } from 'react';

export type BannerVariant = 'info' | 'success' | 'warning' | 'error';

interface BannerProps {
  variant: BannerVariant;
  title?: string;
  children: ReactNode;
  icon?: ReactNode;
  cta?: { label: string; onClick: () => void };
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const VARIANT_CLASSES: Record<BannerVariant, string> = {
  info: 'bg-[var(--info-100)] border-[var(--info-200)] text-[var(--info-700)]',
  success: 'bg-[var(--success-100)] border-[var(--success-200)] text-[var(--success-700)]',
  warning: 'bg-[var(--warning-100)] border-[var(--warning-200)] text-[var(--warning-700)]',
  error: 'bg-[var(--error-100)] border-[var(--error-200)] text-[var(--error-700)]',
};

const DEFAULT_ICONS: Record<BannerVariant, string> = {
  info: 'pi pi-info-circle',
  success: 'pi pi-check-circle',
  warning: 'pi pi-exclamation-triangle',
  error: 'pi pi-times-circle',
};

const ROLES: Record<BannerVariant, 'status' | 'alert'> = {
  info: 'status',
  success: 'status',
  warning: 'alert',
  error: 'alert',
};

/**
 * Banner persistente para comunicar estado de página/contexto.
 *
 * Cont-4c: usado em estados transitórios de contrato e em alertas
 * de DLQ growing fast.
 *
 * @example
 * <Banner variant="warning" title="Contrato em encerramento" cta={{ label: "Confirmar", onClick: openModal }}>
 *   Iniciado por João Silva. Aguarda confirmação UAD.
 * </Banner>
 */
export default function Banner({
  variant,
  title,
  children,
  icon,
  cta,
  dismissible = false,
  onDismiss,
  className = '',
}: BannerProps) {
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${VARIANT_CLASSES[variant]} ${className}`}
      role={ROLES[variant]}
      aria-live={ROLES[variant] === 'alert' ? 'assertive' : 'polite'}
    >
      <span className="flex-shrink-0 pt-0.5" aria-hidden="true">
        {icon ?? <i className={`${DEFAULT_ICONS[variant]} text-base`} />}
      </span>

      <div className="min-w-0 flex-1 text-sm">
        {title && <p className="mb-0.5 font-semibold">{title}</p>}
        <div className="opacity-90">{children}</div>
      </div>

      {cta && (
        <button
          type="button"
          onClick={cta.onClick}
          className="ml-auto flex-shrink-0 rounded-md px-3 py-1 text-sm font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-current"
        >
          {cta.label}
        </button>
      )}

      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          className="ml-2 flex-shrink-0 opacity-60 hover:opacity-100"
          aria-label="Fechar aviso"
        >
          <i className="pi pi-times text-sm" />
        </button>
      )}
    </div>
  );
}
